import { get, isEqual, merge, set } from 'lodash';
import type { InputField, Section } from '@common/formEngine/formEngine.types';
import type { ProcessActivityContextData } from '@common/formEngine/processActivityContext.types';
import type { CustomControlData, DefinedCustomControlData } from '@common/interfaces';
import type { DeepPickPath } from '@common/utils/deepPickUtils';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import { AssetCategory, type CustomControlCode } from '@common/enums';
import { isDependsOn } from '@common/formEngine/dependsOn';
import {
    getAssetsKeyOrFieldKey,
    getInputFieldsFromSection,
    getInputFieldsFromSubsection,
    isOptionsField,
} from '@common/formEngine/typedSections';
import { deepPick } from '@common/utils/deepPickUtils';
import { flattenKeys, isPlainObject } from '@common/utils/objectUtils';
import { getMergedActivityData } from '@frontend/components/pages/episodeCategory/carePath/process/processActivity/processActivityUtil';

export function getChangedData<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>({
    changedField,
    hookData,
    initialData,
    previousValue,
    processActivityContextData,
    sections,
    value,
}: {
    changedField: InputField<Data, TCustomControlCode, Version>;
    hookData?: Record<string, unknown>;
    initialData: Data;
    previousValue: unknown;
    processActivityContextData: ProcessActivityContextData<Data, TCustomControlCode, Version>;
    sections: Section<Data, ContentKey, TCustomControlCode, Version>[];
    value: unknown;
}): {
    changedData: Partial<Data>;
    validateKeys: string[];
} {
    // The changed data object is initialized with the new value for the changed field
    const changedData: Partial<Data> = set({}, getAssetsKeyOrFieldKey(changedField.type, changedField.key), value);
    // The changed and validate keys should always include the key of the changed field
    const changedKeys: DeepPickPath<Data>[] = [changedField.key];
    const validateKeys = new Set<string>([changedField.key]);
    const removedAssetFields: InputField<Data, TCustomControlCode, Version>[] = [];

    if (isPlainObject(previousValue) && isPlainObject(value)) {
        const valueKeys = flattenKeys(value);

        for (const valueKey of valueKeys) {
            const previousKeyValue = get(previousValue, valueKey);
            const nextKeyValue = get(value, valueKey);

            if (previousKeyValue !== nextKeyValue) {
                const keyPath = `${changedField.key}.${valueKey}` as DeepPickPath<Data>;
                changedKeys.push(keyPath);
                validateKeys.add(keyPath);
            }
        }
    }

    if (hookData) {
        processActivityContextData = merge(processActivityContextData, hookData);
        const hookDataKeys = flattenKeys(hookData).map((key) => `hookData.${key}`);
        changedKeys.push(...(hookDataKeys as DeepPickPath<Data>[]));
    }

    for (const key of changedKeys) {
        // TODO: CS-24587 Remove casts once `(Defined)CustomControlData` and `Record<string, unknown>` are compatible
        const changedProcessActivityContextData = getMergedActivityData(
            processActivityContextData as DefinedCustomControlData,
            changedData as CustomControlData
        ) as ProcessActivityContextData<Data, TCustomControlCode, Version>;
        for (const section of sections) {
            /*
             * Section include
             */
            if (isDependsOn(section.include) && section.include.fieldKeys.includes(key)) {
                const shouldInclude = section.include.fn(
                    deepPick(changedProcessActivityContextData, section.include.fieldKeys)
                );
                if (!shouldInclude) {
                    for (const field of getInputFieldsFromSection(section)) {
                        changedKeys.push(field.key);
                        validateKeys.add(field.key);
                        const initialValue = get(initialData, field.key);
                        set(changedData, field.key, initialValue);
                    }
                }
            }

            /*
             * Subsection include
             */
            for (const subsection of section.subsections ?? []) {
                if (isDependsOn(subsection.include) && subsection.include.fieldKeys.includes(key)) {
                    const shouldInclude = subsection.include.fn(
                        deepPick(changedProcessActivityContextData, subsection.include.fieldKeys)
                    );
                    if (!shouldInclude) {
                        for (const field of getInputFieldsFromSubsection(subsection)) {
                            changedKeys.push(field.key);
                            validateKeys.add(field.key);
                            const initialValue = get(initialData, field.key);
                            set(changedData, field.key, initialValue);
                        }
                    }
                }
            }

            /*
             * Fields (from sections and subsections)
             */
            for (const field of getInputFieldsFromSection(section)) {
                // Field include
                if (isDependsOn(field.include) && field.include.fieldKeys.includes(key)) {
                    const shouldInclude = field.include.fn(
                        deepPick(changedProcessActivityContextData, field.include.fieldKeys)
                    );
                    if (!shouldInclude) {
                        changedKeys.push(field.key);
                        validateKeys.add(field.key);

                        const fieldData = get(changedProcessActivityContextData, field.key);
                        if (isPlainObject(fieldData)) {
                            const fieldKeys = flattenKeys(fieldData);
                            for (const fieldKey of fieldKeys) {
                                const keyPath = `${field.key}.${fieldKey}` as DeepPickPath<Data>;
                                changedKeys.push(keyPath);
                                validateKeys.add(keyPath);
                            }
                        }

                        const initialValue = get(initialData, field.key);
                        set(changedData, field.key, initialValue);
                        if (field.type === 'assets') {
                            removedAssetFields.push(field);
                        }
                    }
                }

                // Field isRequired
                if (
                    isDependsOn<Data, boolean, TCustomControlCode, Version>(field.isRequired) &&
                    field.isRequired.fieldKeys.includes(key)
                ) {
                    const isRequired = field.isRequired.fn(
                        deepPick(changedProcessActivityContextData, field.isRequired.fieldKeys)
                    );
                    if (!isRequired) {
                        validateKeys.add(field.key);
                    }
                }

                // Field valueOptions
                if (
                    isOptionsField(field) &&
                    isDependsOn<Data, string[], TCustomControlCode, Version>(field.valueOptions) &&
                    field.valueOptions.fieldKeys.includes(key)
                ) {
                    const oldOptions = field.valueOptions.fn(
                        deepPick(processActivityContextData, field.valueOptions.fieldKeys)
                    );
                    const newOptions = field.valueOptions.fn(
                        deepPick(changedProcessActivityContextData, field.valueOptions.fieldKeys)
                    );
                    if (!isEqual(oldOptions, newOptions)) {
                        changedKeys.push(field.key);
                        const initialValue = get(initialData, field.key);
                        set(changedData, field.key, initialValue);
                    }
                }
            }
        }
    }

    // Clean-up assets
    if (removedAssetFields.length > 0) {
        const originalAssets = (processActivityContextData.assets ?? []) as ProcessActivityAsset[];
        const newAssets = excludeRemovedAssets(originalAssets, removedAssetFields);
        validateKeys.add('assets' as DeepPickPath<Data>);
        set(changedData, 'assets', newAssets);
    }

    return {
        changedData,
        validateKeys: [...validateKeys],
    };
}

function excludeRemovedAssets<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(assets: ProcessActivityAsset[], removedAssetFields: InputField<Data, TCustomControlCode, Version>[]) {
    let newAssets = [...assets];
    for (const removedAssetField of removedAssetFields) {
        const assetsToRemove = newAssets.filter((asset) => !asset.category || asset.category === removedAssetField.key);
        for (const asset of assetsToRemove) {
            if (asset.id) {
                const removeAsset = { ...asset, category: AssetCategory.removed };
                newAssets = newAssets.map((a) => (a.id === asset.id ? removeAsset : a));
            } else {
                newAssets = newAssets.filter((a) => a !== asset);
            }
        }
    }

    return newAssets;
}
